import { supabase } from './supabaseClient';

const DEFAULT_FREE_SHARED_SLOT_LIMIT = 25;
export const SHARED_SLOT_LIMIT_REACHED_MESSAGE = 'Raggiunto il numero massimo di slot disponibili';

export class SharedSlotLimitReachedError extends Error {
  readonly code = 'shared_slot_limit_reached';

  constructor() {
    super(SHARED_SLOT_LIMIT_REACHED_MESSAGE);
    this.name = 'SharedSlotLimitReachedError';
  }
}

function normalizePositiveInteger(raw: unknown, fallback: number): number {
  const value = Math.trunc(Number(raw));
  if (!Number.isFinite(value) || value <= 0) return fallback;
  return value;
}

function normalizeNonNegativeInteger(raw: unknown): number {
  const value = Math.trunc(Number(raw));
  if (!Number.isFinite(value) || value <= 0) return 0;
  return value;
}

export const FREE_SHARED_SLOT_LIMIT = normalizePositiveInteger(
  import.meta.env.VITE_FREE_INVENTORY_LIMIT,
  DEFAULT_FREE_SHARED_SLOT_LIMIT
);

type PlanLimitRow = {
  tier?: string | null;
  inventory_slots?: number | null;
} | null;

export type SharedSlotStatus = {
  usedSlots: number;
  limit: number | null;
  isPro: boolean;
  isAtOrOverLimit: boolean;
  isOverLimit: boolean;
};

function resolveLimitFromPlanRow(row: PlanLimitRow): number | null {
  const tier = String(row?.tier ?? 'free').toLowerCase();
  if (tier === 'pro') return null;

  const planLimit = row?.inventory_slots;
  if (planLimit == null) return FREE_SHARED_SLOT_LIMIT;
  return Math.min(
    normalizePositiveInteger(planLimit, FREE_SHARED_SLOT_LIMIT),
    FREE_SHARED_SLOT_LIMIT
  );
}

/**
 * Returns the effective shared slot limit (inventory + wishlist) for the user.
 * null means unlimited (Pro).
 */
export async function getSharedSlotLimitForUser(userId: string): Promise<number | null> {
  const { data, error } = await supabase
    .from('v_user_plan')
    .select('tier, inventory_slots')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;

  const row = data as PlanLimitRow;
  return resolveLimitFromPlanRow(row);
}

async function sumQuantityForUser(table: 'cards_inventory' | 'wishlist', userId: string): Promise<number> {
  const pageSize = 1000;
  let from = 0;
  let total = 0;

  while (true) {
    const { data, error } = await supabase
      .from(table)
      .select('quantity')
      .eq('user_id', userId)
      .range(from, from + pageSize - 1);
    if (error) throw error;

    const rows = data ?? [];
    for (const row of rows) {
      total += normalizeNonNegativeInteger((row as { quantity?: unknown }).quantity ?? 1);
    }

    if (rows.length < pageSize) break;
    from += pageSize;
  }

  return total;
}

export async function getUsedSharedSlotsForUser(userId: string): Promise<number> {
  const [inventoryCount, wishlistCount] = await Promise.all([
    sumQuantityForUser('cards_inventory', userId),
    sumQuantityForUser('wishlist', userId),
  ]);
  return inventoryCount + wishlistCount;
}

export async function getSharedSlotStatusForUser(userId: string): Promise<SharedSlotStatus> {
  const { data, error } = await supabase
    .from('v_user_plan')
    .select('tier, inventory_slots')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;

  const row = data as PlanLimitRow;
  const tier = String(row?.tier ?? 'free').toLowerCase();
  const isPro = tier === 'pro';
  const limit = resolveLimitFromPlanRow(row);
  const usedSlots = await getUsedSharedSlotsForUser(userId);
  const isAtOrOverLimit = !isPro && limit != null && usedSlots >= limit;
  const isOverLimit = !isPro && limit != null && usedSlots > limit;

  return {
    usedSlots,
    limit,
    isPro,
    isAtOrOverLimit,
    isOverLimit,
  };
}

export function isSharedSlotLimitReachedError(error: unknown): boolean {
  if (error instanceof SharedSlotLimitReachedError) return true;
  const anyError = error as { code?: unknown; message?: unknown; name?: unknown } | null;
  return String(anyError?.code ?? '') === 'shared_slot_limit_reached'
    || String(anyError?.name ?? '') === 'SharedSlotLimitReachedError'
    || String(anyError?.message ?? '') === SHARED_SLOT_LIMIT_REACHED_MESSAGE;
}

export async function assertCanUseSharedSlots(userId: string, additionalSlots: number): Promise<void> {
  const slotsToAdd = normalizeNonNegativeInteger(additionalSlots);
  if (slotsToAdd <= 0) return;

  const status = await getSharedSlotStatusForUser(userId);
  const { limit, usedSlots } = status;
  if (limit == null) return;

  if (usedSlots + slotsToAdd > limit) {
    throw new SharedSlotLimitReachedError();
  }
}

export async function assertCanCreateSharedSlot(userId: string): Promise<void> {
  await assertCanUseSharedSlots(userId, 1);
}
